import { Form, SelectProps, Spin } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

import { _isRequired, parseErrorMessage, renderCompanyListOptions } from "utils";
import { COMPANY_STATUS_ENUM, CompanyEntity } from "interfaces";
import { getCompanyList } from "services";
import { SearchSelect } from "ui";
import { debounce } from "lodash";
import { NormalizedError } from "services/api/errorHandler";

interface IProps {
  inputProps: SelectProps;
  name: string | string[];
  onDataReceived?: (data: CompanyEntity) => void;
  isRulesDisabled?: boolean;
  queryParams?: Record<string, any>;
}

export const CompanySelect = (props: IProps) => {
  const [companies, setCompanies] = useState<CompanyEntity[]>([]);

  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [open, setOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const isSearchingRef = useRef(false);
  const forceKeepOpenRef = useRef(false);

  const preventDropdownClose = useCallback(() => {
    setTimeout(() => {
      setOpen(true);
    }, 0);
  }, []);

  const performSearch = useCallback(
    (value: string) => {
      const fetchData = async () => {
        forceKeepOpenRef.current = true;
        setLoading(true);
        setCompanies([]);
        setHasSearched(true);
        isSearchingRef.current = true;
        preventDropdownClose();

        try {
          const res = await getCompanyList({
            size: 10000,
            page: 0,
            sort: ["companyName,asc"],
            searchTerm: value,
            status: [COMPANY_STATUS_ENUM.ACTIVE, COMPANY_STATUS_ENUM.DRAFT],
            ...props.queryParams,
          });
          if (res.content && res.content.length > 0) {
            setCompanies(res.content ?? []);
          }
          preventDropdownClose();
        } catch (e: unknown) {
          console.error(parseErrorMessage(e as NormalizedError));
        } finally {
          setLoading(false);
          preventDropdownClose();

          setTimeout(() => {
            isSearchingRef.current = false;
            forceKeepOpenRef.current = false;
          }, 300);
        }
      };

      fetchData();
    },
    [preventDropdownClose]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.length >= 3) {
        performSearch(value);
      }
    }, 500),
    [performSearch]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (searchValue.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          forceKeepOpenRef.current = true;
          debouncedSearch.cancel();
          performSearch(searchValue);

          preventDropdownClose();
          return false;
        }
      }
    },
    [debouncedSearch, performSearch, preventDropdownClose, searchValue]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDropdownVisibleChange = useCallback(
    (visible: boolean) => {
      if (forceKeepOpenRef.current && !visible) {
        preventDropdownClose();
        return;
      }
      setOpen(visible);
    },
    [preventDropdownClose]
  );

  const handleSelect = useCallback(
    (value: any, option: any) => {
      if (isSearchingRef.current) {
        preventDropdownClose();
        return false;
      }

      const companyData = companies.find((company) =>
        props.inputProps.labelInValue ? company.id === value.value : company.id === value
      );
      if (companyData && props.onDataReceived) {
        props.onDataReceived(companyData);
      }

      if (props.inputProps.onSelect) {
        props.inputProps.onSelect(value, option);
      }
    },
    [companies, preventDropdownClose, props]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <Form.Item
      name={props.name}
      rules={!props.isRulesDisabled ? [_isRequired("Компания обязательна для ввода")] : undefined}
      className="full-width"
    >
      <SearchSelect
        {...props.inputProps}
        placeholder={
          !props.inputProps.placeholder
            ? "Начните вводить название компании, ОГРН, ОГРНИП, ИНН"
            : props.inputProps.placeholder
        }
        disabled={props.inputProps.disabled}
        loading={loading}
        optionLabelProp="label"
        onSearch={handleSearch}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        open={open}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        filterOption={false}
        showSearch
        defaultActiveFirstOption={false}
        notFoundContent={
          loading ? (
            <Spin size="small" />
          ) : companies.length === 0 ? (
            hasSearched ? (
              "Нет данных"
            ) : (
              "Начните вводить символы для поиска (мин. 3 символа)"
            )
          ) : null
        }
      >
        {renderCompanyListOptions(companies)}
      </SearchSelect>
    </Form.Item>
  );
};
